import * as TabsPrimitive from '@radix-ui/react-tabs'
import { NavLink } from '@remix-run/react'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

const Tabs = TabsPrimitive.Root

const tabsListVariants = cva(
	'inline-flex h-10 items-center justify-center bg-muted text-muted-foreground',
	{
		variants: {
			variant: {
				default: 'bg-muted rounded-md p-1',
				sidebar:
					'bg-sidebar-background border-b border-sidebar-border p-0 w-full',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

const TabsList = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.List>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
		VariantProps<typeof tabsListVariants>
>(({ className, variant = 'default', ...props }, ref) => (
	<TabsPrimitive.List
		ref={ref}
		className={cn(tabsListVariants({ variant }), className)}
		{...props}
	/>
))
TabsList.displayName = TabsPrimitive.List.displayName

const tabsTriggerVariants = cva(
	'inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-base font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
	{
		variants: {
			variant: {
				default:
					'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm rounded-sm',
				sidebar:
					'flex-1 data-[state=active]:font-bold data-[state=active]:text-secondary-foreground data-[state=active]:border-b-4 data-[state=active]:border-primary',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	},
)

const TabsTrigger = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
		VariantProps<typeof tabsTriggerVariants>
>(({ className, variant = 'default', ...props }, ref) => (
	<TabsPrimitive.Trigger
		ref={ref}
		className={cn(tabsTriggerVariants({ variant }), className)}
		{...props}
	/>
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsTriggerNavLink = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Trigger>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
		VariantProps<typeof tabsTriggerVariants> & {
			to: string
		}
>(({ className, variant = 'default', to, children, ...props }, ref) => (
	<TabsTrigger
		ref={ref}
		className={cn(tabsTriggerVariants({ variant }), className)}
		{...props}
		asChild
	>
		<NavLink to={to} prefetch="intent">
			{children}
		</NavLink>
	</TabsTrigger>
))
TabsTriggerNavLink.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
	React.ElementRef<typeof TabsPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
	<TabsPrimitive.Content
		ref={ref}
		className={cn(
			'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
			className,
		)}
		{...props}
	/>
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent, TabsTriggerNavLink }
